import "./ProtocolSessions.scss";
import { regular, solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { memo, useEffect, useState } from "react";
import {
  Accordion,
  Alert,
  Breadcrumb,
  Button,
  Card,
  Col,
  Container,
  Form,
  ListGroup,
  Modal,
  Row,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Protocols } from "../../api/Protocols";
import { Tasks } from "../../api/Tasks";
import AppLoader from "../../components/AppLoader";
import AppSearch from "../../components/AppSearch";
import IconButton from "../../components/IconButton";
// import { useAuth } from '../../contexts/auth';
import { useFullScreenLoader } from "../../contexts/fullScreenLoader";
import { useToasts } from "../../contexts/toasts";
import { Protocol, ProtocolSession } from "../../models/Protocol";
import { Task } from "../../models/Task";

type Props = {
  task: Task;
  actions: React.ReactNode;
};

const TaskItem = memo(({ task, actions }: Props) => {
  return (
    <ListGroup.Item className="d-flex align-items-center">
      <div className="me-3">
        <FontAwesomeIcon icon={regular("check-circle")} size="lg" fixedWidth />
      </div>

      <Col>
        <h6 className="mb-0">{task.name}</h6>
      </Col>

      <Col md={3} className="text-end">
        {actions}
      </Col>
    </ListGroup.Item>
  );
});

export default function ProtocolSessions() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();

  const [tasks, setTasks] = useState<Task[] | null>();
  const [protocol, setProtocol] = useState<Protocol | null>();
  const [activeSession, setActiveSession] = useState<string | undefined>();
  const [show, setShow] = useState<boolean>();
  const [searchText, setSearchText] = useState<string>("");
  const [editing, setEditing] = useState<number | undefined>();
  const [validated, setValidated] = useState<boolean>();
  const [newSession, setNewSession] = useState<ProtocolSession>(
    new ProtocolSession()
  );

  // const { loggedUser } = useAuth();

  const { showLoader, hideLoader } = useFullScreenLoader();
  const { showToast } = useToasts();

  useEffect(() => {
    initTasks();
    initProtocol();
    // eslint-disable-next-line
  }, []);

  const initTasks = () => {
    Tasks.getTasks().then(
      ({ data: tasks }) => setTasks(tasks),
      (err) => {
        const message = err.response?.data?.message || err.message;
        const status = err.response?.status;
        showToast!(
          "tasks-error",
          message,
          `Tasks Error ${status ? `(${status})` : ""}`,
          "danger"
        );
      }
    );
  };

  const handleModalClose = () => {
    setShow(false);
    setNewSession(new ProtocolSession());
  };

  const handleSubmit = (evt: React.FormEvent<HTMLFormElement>) => {
    const form = evt.currentTarget;
    evt.preventDefault();
    evt.stopPropagation();
    setValidated(true);

    if (form.checkValidity() === true) {
      const newSessions = [...protocol!.sessions];

      if (editing === undefined) newSessions.push(newSession);
      else newSessions[editing] = newSession;

      setProtocol({
        ...protocol!,
        sessions: newSessions,
      });
      setValidated(false);
      handleModalClose();
    }
  };

  const handleFieldChange =
    (field: keyof ProtocolSession) =>
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      const value = evt.target.value;
      setNewSession({
        ...newSession,
        [field]: value,
      });
    };

  const initProtocol = () => {
    Protocols.getProtocol(Number(id)!).then(
      ({ data: protocol }) => {
        if (protocol.sessions.length === 0)
          protocol.sessions.push(new ProtocolSession("Session #1"));
        setProtocol(protocol);
      },
      (err) => {
        const message = err.response?.data?.message || err.message;
        const status = err.response?.status;
        showToast!(
          "protocol-error",
          message,
          `Protocol Error ${status ? `(${status})` : ""}`,
          "danger"
        );
      }
    );
  };

  const handleActiveSessionChange = (
    eventKey: string | string[] | null | undefined,
    evt: Object
  ) => {
    setActiveSession(
      Array.isArray(eventKey) ? eventKey[0] : (eventKey as string)
    );
  };

  const swapTasks =
    (session: number, source: number, direction: "up" | "down") => () => {
      const newSessions = [...protocol!.sessions];
      const target = direction === "down" ? source + 1 : source - 1;
      newSessions[session].tasks[target] = newSessions[session].tasks.splice(
        source,
        1,
        newSessions[session].tasks[target]
      )[0];
      setProtocol({
        ...protocol!,
        sessions: newSessions,
      });
    };

  const removeTask = (session: number, task: number) => () => {
    const newSessions = [...protocol!.sessions];
    newSessions[session].tasks.splice(task, 1);
    setProtocol({
      ...protocol!,
      sessions: newSessions,
    });
  };

  const addSession = () => {
    const newSession = new ProtocolSession(
      `Session #${protocol!.sessions.length + 1}`
    );
    setProtocol({
      ...protocol!,
      sessions: protocol!.sessions.concat(newSession),
    });
  };

  const editSession = (session: number) => (evt: React.MouseEvent) => {
    evt.stopPropagation();
    setEditing(session);
    setNewSession({ ...protocol!.sessions[session] });
    setShow(true);
  };

  const removeSession = (session: number) => (evt: React.MouseEvent) => {
    evt.stopPropagation();
    const newSessions = [...protocol!.sessions];
    newSessions.splice(session, 1);
    setProtocol({
      ...protocol!,
      sessions: newSessions,
    });
  };

  const renderProtocolSessions = () => {
    if (protocol!.sessions.length === 0)
      return (
        <Alert className="py-2 mb-0">
          <FontAwesomeIcon icon={solid("info-circle")} />
          <span className="ms-2">No sessions available.</span>
        </Alert>
      );

    return (
      <Accordion
        className="sessions-accordions"
        activeKey={activeSession}
        onSelect={handleActiveSessionChange}
      >
        {protocol?.sessions.map((session, i) => (
          <Accordion.Item key={i} eventKey={`${i}`}>
            <Accordion.Header className="d-flex">
              <Col>{session.label}</Col>

              <div className="text-muted p-1 me-2" onClick={editSession(i)}>
                <FontAwesomeIcon icon={solid("pen")} fixedWidth />
              </div>
              <div className="text-danger p-1 me-3" onClick={removeSession(i)}>
                <FontAwesomeIcon icon={solid("trash")} fixedWidth />
              </div>
            </Accordion.Header>

            <Accordion.Body className="p-1">
              {session.tasks.length === 0 && (
                <Alert className="py-2 mb-0">
                  <FontAwesomeIcon icon={solid("info-circle")} />
                  <span className="ms-2">No tasks in this session.</span>
                </Alert>
              )}

              {session.tasks!.length > 0 && (
                <ListGroup>
                  {session.tasks.map((task, j) => (
                    <TaskItem
                      key={task.id}
                      task={task}
                      actions={
                        <>
                          {j < session.tasks.length - 1 && (
                            <IconButton onClick={swapTasks(i, j, "down")}>
                              <FontAwesomeIcon icon={solid("arrow-down")} />
                            </IconButton>
                          )}
                          {j > 0 && (
                            <IconButton
                              className="ms-2"
                              onClick={swapTasks(i, j, "up")}
                            >
                              <FontAwesomeIcon icon={solid("arrow-up")} />
                            </IconButton>
                          )}
                          <IconButton
                            className="ms-2 text-danger"
                            onClick={removeTask(i, j)}
                          >
                            <FontAwesomeIcon icon={solid("times")} />
                          </IconButton>
                        </>
                      }
                    />
                  ))}
                </ListGroup>
              )}
            </Accordion.Body>
          </Accordion.Item>
        ))}
      </Accordion>
    );
  };

  const addTask = (task: Task) => () => {
    if (!activeSession) {
      showToast!(
        "task-warning",
        "You must select a session before adding a task.",
        "Select Session",
        "warning"
      );
      return;
    }

    const newSessions = [...(protocol!.sessions as ProtocolSession[])];
    const found = protocol!.sessions[+activeSession].tasks.findIndex(
      (t) => t.id === task.id
    );
    if (found > -1) {
      showToast!(
        "task-warning",
        "Task already present in selected session.",
        "Existing Task",
        "warning"
      );
      return;
    }

    protocol!.sessions[+activeSession].tasks.push(task);
    setProtocol({
      ...protocol!,
      sessions: newSessions,
    });
  };

  const saveProtocolSessions = () => {
    showLoader!();

    const body = { ...protocol! };
    body.sessions = body.sessions.map((session: any) => {
      session.tasks = session.tasks.map((task: any) => task.id);
      return session;
    });
    body.id = Number(id)!; // TODO check on the backend the changing id

    Protocols.updateProtocol(body).then(
      () => {
        hideLoader!();
        showToast!(
          "protocol-updated",
          "Protocol updated successfully",
          "Protocol updated",
          "success"
        );
        navigate("/protocols");
      },
      (err) => {
        hideLoader!();
        const message = err.response?.data?.message || err.message;
        const status = err.response?.status;
        showToast!(
          "protocol-error",
          message,
          `Protocol Error ${status ? `(${status})` : ""}`,
          "danger"
        );
      }
    );
  };

  const handleSearchChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const value = evt.target.value;
    setSearchText(value);
  };

  const renderTasks = () => {
    if (tasks!.length === 0)
      return (
        <Alert className="py-2 mb-0">
          <FontAwesomeIcon icon={solid("info-circle")} />
          <span className="ms-2">{t("tasksList.noTasks")}</span>
        </Alert>
      );

    return (
      <>
        <AppSearch
          className="mb-3"
          value={searchText}
          onChange={handleSearchChange}
        />
        <ListGroup className="task-pick-list">
          {tasks!
            .filter((task) =>
              task.name.toLowerCase().includes(searchText.toLowerCase())
            )
            .map((task) => (
              <TaskItem
                key={task.id}
                task={task}
                actions={
                  <IconButton className="text-success" onClick={addTask(task)}>
                    <FontAwesomeIcon icon={solid("plus")} />
                  </IconButton>
                }
              />
            ))}
        </ListGroup>
      </>
    );
  };

  const renderCardContent = () => {
    return (
      <>
        <Card.Body>
          <Row>
            <Col>
              <div className="d-flex align-items-center mb-2">
                <Col>
                  <h6 className="mb-0">Protocol Sessions</h6>
                </Col>

                <Button variant="success" size="sm" onClick={addSession}>
                  <FontAwesomeIcon icon={solid("plus")} />
                  <span className="ms-2">Add Session</span>
                </Button>
              </div>
              {renderProtocolSessions()}
            </Col>
            <Col>
              <h6 className="pt-1 pb-2">Available Tasks</h6>
              {renderTasks()}
            </Col>
          </Row>
        </Card.Body>
        <Card.Footer className="d-flex justify-content-between bg-white">
          <Button size="sm" variant="light" className="border" onClick={back}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="success"
            className="px-3"
            onClick={saveProtocolSessions}
          >
            <FontAwesomeIcon icon={solid("save")} />
            <span className="ms-2">Save</span>
          </Button>
        </Card.Footer>
      </>
    );
  };

  const renderAsyncContent = () => {
    return tasks && protocol ? (
      renderCardContent()
    ) : (
      <div className="d-flex py-3 justify-content-center">
        <AppLoader size={150} />
      </div>
    );
  };

  const back = () => {
    return navigate('/protocols')
  };

  return (
    <Container>
      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/protocols" }}>
          {t("pages.protocols")}
        </Breadcrumb.Item>
        <Breadcrumb.Item active>
          "{protocol?.name}" {t("pages.tasks")}
        </Breadcrumb.Item>
      </Breadcrumb>

      <Card>{renderAsyncContent()}</Card>

      <Modal show={show} onHide={handleModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>
            {editing !== undefined ? t("common.edit") : t("common.create")}{" "}
            {t("common.protocol")}
          </Modal.Title>
        </Modal.Header>

        <Form validated={validated} noValidate onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group controlId="session-name">
              <Form.Label>{t("common.name")}</Form.Label>
              <Form.Control
                placeholder={t("protocolSessions.sessionName")}
                size="sm"
                value={newSession.label}
                onChange={handleFieldChange("label")}
                required
              />
              <Form.Control.Feedback type="invalid">
                {t("errors.requiredField", {
                  field: t("protocolSessions.protocolName"),
                })}
              </Form.Control.Feedback>
            </Form.Group>
          </Modal.Body>

          <Modal.Footer>
            <Button
              variant="light"
              className="border"
              size="sm"
              onClick={handleModalClose}
            >
              {t("common.cancel")}
            </Button>
            <Button variant="success" size="sm" type="submit">
              {t("common.confirm")}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
}
